import type { Module } from "vuex";
import type { RouteLocation } from "vue-router";

import type { Client } from "@/types/types";

import { MultiviewMode, type MultiviewModeType } from "@/enums/multiview";

import type { RootState } from "@/store/types";

const storeNamespace = "ui";

export const UiAction = {
    CloseSidebar: `${storeNamespace}/closeSidebar`,
    MultiviewDestroy: `${storeNamespace}/multiviewDestroy`,
    MultiviewInit: `${storeNamespace}/multiviewInit`,
    MultiviewUpdate: `${storeNamespace}/multiviewUpdate`,
    OpenSidebar: `${storeNamespace}/openSidebar`,
    SetCurrentClient: `${storeNamespace}/setCurrentClient`,
    SetSidebarWidthCollapsed: `${storeNamespace}/setSidebarWidthCollapsed`,
    SetSidebarWidthOpen: `${storeNamespace}/setSidebarWidthOpen`,
    SetTheme: `${storeNamespace}/setTheme`,
    SetThemeMode: `${storeNamespace}/setThemeMode`,
    SetShowAllContent: `${storeNamespace}/setShowAllContent`,
    SetLoginReturnRoute: `${storeNamespace}/setLoginReturnRoute`,
    SetNetworkErrorReturnRoute: `${storeNamespace}/setNetworkErrorReturnRoute`
};

const UiInternalAction = {
    MultiviewUpdate: "multiviewUpdate"
};

export const UiMutation = {
    MultiviewDestroy: "multiviewDestroy",
    MultiviewInit: "multiviewInit",
    MultiviewSetPageCount: "multiviewSetPageCount",
    MultiviewSetSelected: "multiviewSetSelected",
    MultiviewSetTotalCount: "multiviewSetTotalCount",
    MultiviewSwitchView: "multiviewSwitchView",
    SetCurrentClient: "setCurrentClient",
    SetSidebarState: "setSidebarState",
    SetSidebarWidthCollapsed: "setSidebarWidthCollapsed",
    SetSidebarWidthOpen: "setSidebarWidthOpen",
    UpdateSizeFilter: "updateSizeFilter",
    SetTheme: "setTheme",
    SetThemeMode: "setThemeMode",
    SetShowAllContent: "setShowAllContent"
};

type InstanceState = {
    pageCount?: number;
    selected?: number;
    totalCount?: number;
    view?: MultiviewModeType;
};

type Themes = "default" | "light" | "campaign-light" | "templates-light" | "login";

export type ThemeMode =
    | {
          theme: "light";
          icon: "md-moon";
          label: "Dark mode";
      }
    | {
          theme: "dark";
          icon: "md-sunny";
          label: "Light mode";
      };

export type State = {
    defaultTheme: Themes;
    currentClient: Client | null;
    isSidebarOpen: boolean;
    loginReturnRoute: RouteLocation | null;
    networkErrorReturnRoute: string | null;
    sidebarWidthOpen: number;
    sidebarWidthCollapsed: number;
    theme: string;
    themeMode: ThemeMode;
    showAllContent: boolean;
    multiviewComponents: Record<string, InstanceState>;
};

const instanceState = {
    pageCount: 0,
    selected: 0,
    totalCount: 0,
    view: MultiviewMode.Grid
};

const uiModule: Module<State, RootState> = {
    namespaced: true,
    state: {
        defaultTheme: "default",
        currentClient: null,
        isSidebarOpen: false,
        loginReturnRoute: null,
        networkErrorReturnRoute: null,
        sidebarWidthOpen: 250,
        sidebarWidthCollapsed: 0,
        theme: "default",
        themeMode: {
            theme: "light",
            icon: "md-moon",
            label: "Dark mode"
        },
        showAllContent: false,
        multiviewComponents: {}
    },

    mutations: {
        multiviewDestroy(state, instance: string) {
            delete state.multiviewComponents[instance];
        },

        multiviewInit(state, instance: string) {
            if (!(instance in state.multiviewComponents)) {
                state.multiviewComponents[instance] = { ...instanceState };
            }
        },

        multiviewSetPageCount(state, { instance, pageCount }: { instance: string; pageCount: number }) {
            state.multiviewComponents[instance] = {
                ...state.multiviewComponents[instance],
                pageCount
            };
        },

        multiviewSetSelected(state, { instance, selected }: { instance: string; selected: number }) {
            state.multiviewComponents[instance] = {
                ...state.multiviewComponents[instance],
                selected
            };
        },

        multiviewSetTotalCount(state, { instance, totalCount }: { instance: string; totalCount: number }) {
            state.multiviewComponents[instance] = {
                ...state.multiviewComponents[instance],
                totalCount
            };
        },

        multiviewSwitchView(state, { instance, view }: { instance: string; view: MultiviewModeType }) {
            state.multiviewComponents[instance] = {
                ...state.multiviewComponents[instance],
                view
            };
        },

        setCurrentClient(state, client: Client | null) {
            state.currentClient = client;
        },

        setLoginReturnRoute(state, route: RouteLocation | null) {
            state.loginReturnRoute = route;
        },

        setNetworkErrorReturnRoute(state, route: string | null) {
            state.networkErrorReturnRoute = route;
        },

        setSidebarState(state, payload: boolean) {
            state.isSidebarOpen = payload;
        },

        setSidebarWidthOpen(state, width: number) {
            state.sidebarWidthOpen = width;
        },

        setSidebarWidthCollapsed(state, width: number) {
            state.sidebarWidthCollapsed = width;
        },

        setTheme(state, theme: Themes) {
            state.theme = theme;
        },

        setThemeMode(state, payload: State["themeMode"]) {
            state.themeMode = payload;
        },

        setShowAllContent(state, payload: boolean) {
            state.showAllContent = payload;
        }
    },

    actions: {
        closeSidebar({ commit }) {
            commit(UiMutation.SetSidebarState, false);
        },

        multiviewDestroy({ commit }, instance: string) {
            commit(UiMutation.MultiviewDestroy, instance);
        },

        multiviewInit({ commit, dispatch }, initData: { instance: string; view: MultiviewModeType }) {
            commit(UiMutation.MultiviewInit, initData.instance);
            return dispatch(UiInternalAction.MultiviewUpdate, initData);
        },

        multiviewUpdate(
            { commit },
            update: {
                instance: string;
                view?: MultiviewModeType;
                pageCount?: number;
                selected?: number;
                totalCount?: number;
            }
        ) {
            if (!("instance" in update)) {
                throw Error("Instance name required");
            }

            if ("pageCount" in update) {
                commit(UiMutation.MultiviewSetPageCount, update);
            }

            if ("selected" in update) {
                commit(UiMutation.MultiviewSetSelected, update);
            }

            if ("totalCount" in update) {
                commit(UiMutation.MultiviewSetTotalCount, update);
            }

            if ("view" in update) {
                commit(UiMutation.MultiviewSwitchView, update);
            }
        },

        openSidebar({ commit }) {
            commit(UiMutation.SetSidebarState, true);
        },

        setCurrentClient({ commit }, client: string) {
            commit(UiMutation.SetCurrentClient, client);
        },

        setSidebarWidthCollapsed({ commit }, width: number) {
            commit(UiMutation.SetSidebarWidthCollapsed, width);
        },

        setSidebarWidthOpen({ commit }, width: number) {
            commit(UiMutation.SetSidebarWidthOpen, width);
        },

        setTheme({ commit }, theme: Themes) {
            commit(UiMutation.SetTheme, theme);
        },

        setThemeMode({ commit }, value: State["themeMode"]) {
            localStorage.setItem("currentTheme", JSON.stringify(value));
            commit(UiMutation.SetThemeMode, value);
        },

        setShowAllContent({ commit }, value: boolean) {
            commit(UiMutation.SetShowAllContent, value);
        }
    }
};

export default uiModule;
